import { Box, Card, Code, Heading, Stack, Text } from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"

import { usePermissions } from "@/hooks/usePermissions"
import { useAuthStore } from "@/stores/auth"

export const Route = createFileRoute("/_layout/debug-permissions")({
  component: DebugPermissions,
})

function DebugPermissions() {
  const { user } = useAuthStore()
  const permissions = usePermissions()

  return (
    <Box>
      <Heading size="lg" mb={6}>
        Debug: Your Current Permissions
      </Heading>

      <Stack gap={4}>
        <Card.Root>
          <Card.Header>
            <Heading size="md">User Information</Heading>
          </Card.Header>
          <Card.Body>
            <Text>
              Email: <Code>{user?.email}</Code>
            </Text>
            <Text>
              Full Name: <Code>{user?.full_name}</Code>
            </Text>
            <Text>
              Is Admin: <Code>{permissions.isAdmin ? "Yes" : "No"}</Code>
            </Text>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header>
            <Heading size="md">All Scopes from JWT Token</Heading>
          </Card.Header>
          <Card.Body>
            <Stack gap={2}>
              {permissions.scopes.length === 0 ? (
                <Text color="red.500">
                  No scopes found! You may need to logout and login again.
                </Text>
              ) : (
                permissions.scopes.map((scope) => (
                  <Code key={scope} fontSize="sm">
                    {scope}
                  </Code>
                ))
              )}
            </Stack>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header>
            <Heading size="md">Student Permissions</Heading>
          </Card.Header>
          <Card.Body>
            <Stack gap={2}>
              <Text>
                Can Create Students:{" "}
                <Code
                  colorPalette={
                    permissions.hasPermission("students:create")
                      ? "green"
                      : "red"
                  }
                >
                  {permissions.hasPermission("students:create")
                    ? "Yes ✅"
                    : "No ❌"}
                </Code>
              </Text>
              <Text>
                Can Read Students (all):{" "}
                <Code
                  colorPalette={
                    permissions.hasPermission("students:read") ? "green" : "red"
                  }
                >
                  {permissions.hasPermission("students:read")
                    ? "Yes ✅"
                    : "No ❌"}
                </Code>
              </Text>
              <Text>
                Can Read Own Students:{" "}
                <Code
                  colorPalette={
                    permissions.hasPermission("students:read_own")
                      ? "green"
                      : "red"
                  }
                >
                  {permissions.hasPermission("students:read_own")
                    ? "Yes ✅"
                    : "No ❌"}
                </Code>
              </Text>
              <Text>
                Can Update Students (all):{" "}
                <Code
                  colorPalette={
                    permissions.hasPermission("students:update")
                      ? "green"
                      : "red"
                  }
                >
                  {permissions.hasPermission("students:update")
                    ? "Yes ✅"
                    : "No ❌"}
                </Code>
              </Text>
              <Text>
                Can Update Own Students:{" "}
                <Code
                  colorPalette={
                    permissions.hasPermission("students:update_own")
                      ? "green"
                      : "red"
                  }
                >
                  {permissions.hasPermission("students:update_own")
                    ? "Yes ✅"
                    : "No ❌"}
                </Code>
              </Text>
            </Stack>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header>
            <Heading size="md">Instructions</Heading>
          </Card.Header>
          <Card.Body>
            <Stack gap={2}>
              <Text fontWeight="bold">Expected for Parents:</Text>
              <Text>✅ Can Create Students: Yes</Text>
              <Text>❌ Can Read Students (all): No</Text>
              <Text>✅ Can Read Own Students: Yes</Text>
              <Text>❌ Can Update Students (all): No</Text>
              <Text>✅ Can Update Own Students: Yes</Text>

              <Box mt={4} p={4} bg="yellow.50" borderRadius="md">
                <Text fontWeight="bold" color="yellow.800">
                  If you see "Can Read Students (all): Yes ✅"
                </Text>
                <Text color="yellow.800">
                  → You have the OLD JWT token with buggy permissions
                  <br />→ Please LOGOUT and LOGIN again to get new token with
                  fixed permissions
                </Text>
              </Box>
            </Stack>
          </Card.Body>
        </Card.Root>
      </Stack>
    </Box>
  )
}
